import React, { useState, useRef } from 'react';
import { format } from 'date-fns';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Message {
  id: string;
  message_id: string;
  thread_id: string;
  from: {
    email: string;
    name?: string;
  };
  to: {
    email: string;
    name?: string;
  }[];
  cc?: {
    email: string;
    name?: string;
  }[];
  bcc?: {
    email: string;
    name?: string;
  }[];
  subject: string;
  text_body?: string;
  html_body?: string;
  snippet?: string;
  received_at: string;
  is_read: boolean;
  is_starred?: boolean;
  archived?: boolean;
  intent?: string;
  snooze_until?: string | null;
  assignee?: string | null;
  is_positive_response?: boolean;
  positive_response_reason?: string;
}

interface Thread {
  id: string;
  thread_id: string;
  subject: string;
  messages: Message[];
}

interface ThreadViewProps {
  thread: Thread | null;
  onMarkAsRead: (messageId: string) => void;
  onToggleStar: (messageId: string, starred: boolean) => void;
  onArchive: (threadId: string) => void;
  onMoveToInbox: (threadId: string) => void;
  onSendReply: (threadId: string, messageId: string, content: string) => void;
  onMarkPositiveResponse: (messageId: string, isPositive: boolean, reason?: string) => void;
  onSnoozeMessage?: (messageId: string, untilIso: string) => void;
  onAssignMessage?: (messageId: string, assignee: string) => void;
}

const ThreadView: React.FC<ThreadViewProps> = ({
  thread,
  onMarkAsRead,
  onToggleStar,
  onArchive,
  onMoveToInbox,
  onSendReply,
  onMarkPositiveResponse,
  onSnoozeMessage,
  onAssignMessage
}) => {
  const [replyContent, setReplyContent] = useState('');
  const [isReplying, setIsReplying] = useState(false);
  const [replyingToMessageId, setReplyingToMessageId] = useState<string | null>(null);
  const quillRef = useRef<ReactQuill>(null);

  // State for positive response modal
  const [showPositiveResponseModal, setShowPositiveResponseModal] = useState(false);
  const [currentMessageId, setCurrentMessageId] = useState<string | null>(null);
  const [positiveResponseReason, setPositiveResponseReason] = useState('');
  const [isCurrentlyPositive, setIsCurrentlyPositive] = useState(false);

  if (!thread) {
    return (
      <div className="p-6 text-center">
        <p className="text-lg text-gray-500">
          Select a conversation to view
        </p>
      </div>
    );
  }

  // Format date
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM d, yyyy h:mm a');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get display name
  const getDisplayName = (email: string, name?: string) => {
    if (name && name.trim()) {
      return name;
    }

    // Extract name from email
    return email.split('@')[0];
  };

  // Handle reply button click
  const handleStartReply = (messageId: string) => {
    setIsReplying(true);
    setReplyingToMessageId(messageId);

    // Initialize with a greeting and signature
    const message = thread.messages.find(m => m.id === messageId);
    if (message) {
      const recipientName = getDisplayName(message.from.email, message.from.name);
      setReplyContent(`<p>Hi ${recipientName},</p><p><br></p><p>Best regards,</p>`);
    } else {
      setReplyContent('<p>Hi,</p><p><br></p><p>Best regards,</p>');
    }
  };

  // Send reply
  const handleSendReply = () => {
    if (replyingToMessageId && replyContent.trim()) {
      onSendReply(thread.thread_id, replyingToMessageId, replyContent);
      setIsReplying(false);
      setReplyContent('');
    }
  };

  // Cancel reply
  const handleCancelReply = () => {
    setIsReplying(false);
    setReplyContent('');
  };

  // Open positive response modal
  const handleOpenPositiveResponseModal = (messageId: string, isCurrentPositive: boolean) => {
    const message = thread.messages.find(m => m.id === messageId);
    setCurrentMessageId(messageId);
    setIsCurrentlyPositive(isCurrentPositive);
    setPositiveResponseReason(message?.positive_response_reason || '');
    setShowPositiveResponseModal(true);
  };

  // Handle toggling positive response
  const handleTogglePositiveResponse = (messageId: string, currentValue: boolean) => {
    // If turning off positive response, just toggle it without asking for reason
    if (currentValue) {
      onMarkPositiveResponse(messageId, false);
    } else {
      // If marking as positive, open modal to ask for reason
      handleOpenPositiveResponseModal(messageId, false);
    }
  };
  
  // Save positive response with reason
  const handleSavePositiveResponse = () => {
    if (currentMessageId) {
      onMarkPositiveResponse(currentMessageId, true, positiveResponseReason);
      setShowPositiveResponseModal(false);
      setCurrentMessageId(null);
      setPositiveResponseReason('');
    }
  };
  
  // Cancel positive response modal
  const handleCancelPositiveResponse = () => {
    setShowPositiveResponseModal(false);
    setCurrentMessageId(null);
    setPositiveResponseReason('');
  };

  // Quill editor modules
  const modules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ]
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-gray-200">
      <p className="text-xl font-bold p-4 border-b border-gray-200">
        {thread.subject}
      </p>

      <div className="flex-1 overflow-auto p-4">
        <div className="flex flex-col space-y-6">
          {thread.messages.map((message, index) => (
            <div key={message.id} className={`w-full ${index > 0 ? 'border-t border-gray-200 pt-6' : ''}`}>
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                    {getDisplayName(message.from.email, message.from.name).charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold">
                      {getDisplayName(message.from.email, message.from.name)}
                    </p>
                    <p className="text-sm text-gray-500">
                      To: {message.to.map(recipient => getDisplayName(recipient.email, recipient.name)).join(', ')}
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500">
                    {formatDate(message.received_at)}
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <div
                  dangerouslySetInnerHTML={{ 
                    __html: message.html_body || `<p>${message.text_body || ''}</p>` 
                  }}
                />
              </div>

              <div className="flex items-center justify-end space-x-2">
                {message.intent && (
                  <div className="flex items-center px-3 py-1 rounded bg-blue-100 text-blue-800 text-xs mr-1">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M2 11a1 1 0 011-1h3l3-5 3 5h3a1 1 0 010 2h-3l-3 5-3-5H3a1 1 0 01-1-1z" />
                    </svg>
                    <span>{message.intent}</span>
                  </div>
                )}
                {message.is_positive_response && (
                  <div className="flex items-center px-3 py-1 rounded bg-green-100 text-green-800 text-xs mr-1">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Positive Lead</span>
                  </div>
                )}
                <button
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  title="Mark as read/unread"
                  onClick={() => onMarkAsRead(message.id)}
                >
                  {message.is_read ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                  )}
                </button>

                <button
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  title="Star message"
                  onClick={() => onToggleStar(message.id, !message.is_starred)}
                >
                  {message.is_starred ? (
                    <svg className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
                    </svg>
                  )}
                </button>

                <button
                  className="p-2 rounded-full hover:bg-gray-100 text-gray-600"
                  title={(message.archived) ? "Move to inbox" : "Archive"}
                  onClick={() => (message.archived) ? onMoveToInbox(thread.thread_id) : onArchive(thread.thread_id)}
                >
                  {(message.archived) ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
                    </svg>
                  )}
                </button>

                <button 
                  className="flex items-center space-x-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                  onClick={() => handleStartReply(message.id)}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"></path>
                  </svg>
                  <span>Reply</span>
                </button>

                <button 
                  className={`flex items-center space-x-1 px-3 py-1 rounded ${message.is_positive_response ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} text-sm`}
                  onClick={() => handleTogglePositiveResponse(message.id, !!message.is_positive_response)}
                  title={message.is_positive_response ? "Remove positive response flag" : "Mark as positive response"}
                >
                  {message.is_positive_response ? (
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  )}
                  <span>{message.is_positive_response ? "Lead" : "Mark Lead"}</span>
                </button>

                {onSnoozeMessage && (
                  <button
                    className="flex items-center space-x-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                    title="Snooze"
                    onClick={() => {
                      // Simple presets: 1 day, 2 days, 1 week
                      const choice = window.prompt('Snooze until (preset: 1d,2d,1w) or ISO datetime (YYYY-MM-DDTHH:mm):', '1d');
                      if (!choice) return;
                      const now = new Date();
                      let until = new Date(now);
                      if (choice === '1d') until.setDate(now.getDate() + 1);
                      else if (choice === '2d') until.setDate(now.getDate() + 2);
                      else if (choice === '1w') until.setDate(now.getDate() + 7);
                      else {
                        const parsed = new Date(choice);
                        if (!isNaN(parsed.getTime())) until = parsed; else return;
                      }
                      onSnoozeMessage(message.id, until.toISOString());
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <span>Snooze</span>
                  </button>
                )}

                {onAssignMessage && (
                  <button
                    className="flex items-center space-x-1 px-3 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                    title="Assign"
                    onClick={() => {
                      const assignee = window.prompt('Assign to (email or name):');
                      if (assignee) onAssignMessage(message.id, assignee);
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    </svg>
                    <span>Assign</span>
                  </button>
                )}
              </div>

              {isReplying && replyingToMessageId === message.id && (
                <div className="mt-4 p-4 border border-gray-200 rounded-md">
                  <ReactQuill 
                    ref={quillRef}
                    value={replyContent}
                    onChange={setReplyContent}
                    modules={modules}
                    style={{ height: '200px', marginBottom: '10px' }}
                  />
                  <div className="flex items-center mt-4 space-x-2">
                    <div className="flex-1" />
                    <button 
                      onClick={handleCancelReply} 
                      className="px-3 py-1 border border-gray-300 rounded text-sm hover:bg-gray-100"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleSendReply} 
                      className={`px-3 py-1 rounded text-sm text-white ${replyContent.trim() ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed'}`}
                      disabled={!replyContent.trim()}
                    >
                      Send
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Positive Response Modal */}
      {showPositiveResponseModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {isCurrentlyPositive ? 'Edit Lead Information' : 'Mark as Positive Lead'}
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Why is this a positive lead? (Notes)
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                rows={4}
                value={positiveResponseReason}
                onChange={(e) => setPositiveResponseReason(e.target.value)}
                placeholder="e.g., Requested pricing, Showed interest in product, Asked for demo"
              />
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={handleCancelPositiveResponse}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePositiveResponse}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreadView;
